/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys match existing DB/API schema */
import type React from 'react';
import { Static, Type } from '@sinclair/typebox';
import { RenderMode } from '../website_types/WebsiteTypes';
import { ComponentMeta } from '../website_types/BuilderTypes';
import { ProfessionalBindingData } from '../website_types/Bindings';
import { EmailStylesSchema } from './EmailStyles';

// Structural interface for typebox schemas - avoids Symbol resolution issues across packages
interface TypeboxSchema {
  static: unknown;
  params: unknown[];
  properties?: Record<string, unknown>;
}

// Extract static type from schema without requiring TSchema constraint
type InferStatic<T extends TypeboxSchema> = T['static'];

// Display data for an email-prestation block, keyed by prestationId.
// Price/duration are pre-formatted (backend at send-time, catalog `price_string`
// in the builder preview) so the renderer never formats prices itself.
export type EmailPrestationDisplay = {
  name: string;
  price?: string;
  duration?: string;
};
export type EmailPrestationDataMap = Record<string, EmailPrestationDisplay>;

export type EmailComponentRuntimeProps<T extends TypeboxSchema, TVariant extends string> = {
  variant: TVariant;
  values: Partial<InferStatic<T>>;
  resolved: InferStatic<T>;
  mode: RenderMode;
  config: EmailConfig;
  proData?: ProfessionalBindingData;
  translateFn?: (key: string) => string;
  prestationData?: EmailPrestationDataMap;
};

export type EmailBuilderExport<T extends TypeboxSchema, TVariant extends string> = {
  meta: ComponentMeta<T, TVariant, keyof InferStatic<T> & string>;
  Render: React.FC<EmailComponentRuntimeProps<T, TVariant>>;
};

export const EMAIL_CONFIG_VERSION = '1.0.0';

export const EMAIL_COMPONENT_IDS = {
  HEADER: 'email-header',
  TWO_COLUMNS: 'email-two-columns',
  TEXT: 'email-text',
  BUTTON: 'email-button',
  IMAGE: 'email-image',
  DIVIDER: 'email-divider',
  SOCIAL: 'email-social',
  PRESTATION: 'email-prestation',
  FOOTER: 'email-footer',
  // Blocs V2 (bibliothèque de modèles) : bandeau de couverture, trois colonnes, chiffre clé, date limite.
  HERO: 'email-hero',
  THREE_COLUMNS: 'email-three-columns',
  STAT: 'email-stat',
  DEADLINE: 'email-deadline',
  // Code de la promotion jointe à une campagne, et bouton qui l'applique dans le tunnel.
  PROMO_CODE: 'email-promo-code',
} as const;

export type EmailComponentId = (typeof EMAIL_COMPONENT_IDS)[keyof typeof EMAIL_COMPONENT_IDS];

export const EMAIL_COMPONENT_LABEL_KEYS: Record<EmailComponentId, string> = {
  [EMAIL_COMPONENT_IDS.HEADER]: 'EMAIL_BUILDER_COMPONENT_HEADER',
  [EMAIL_COMPONENT_IDS.TWO_COLUMNS]: 'EMAIL_BUILDER_COMPONENT_TWO_COLUMNS',
  [EMAIL_COMPONENT_IDS.TEXT]: 'EMAIL_BUILDER_COMPONENT_TEXT',
  [EMAIL_COMPONENT_IDS.BUTTON]: 'EMAIL_BUILDER_COMPONENT_BUTTON',
  [EMAIL_COMPONENT_IDS.IMAGE]: 'EMAIL_BUILDER_COMPONENT_IMAGE',
  [EMAIL_COMPONENT_IDS.DIVIDER]: 'EMAIL_BUILDER_COMPONENT_DIVIDER',
  [EMAIL_COMPONENT_IDS.SOCIAL]: 'EMAIL_BUILDER_COMPONENT_SOCIAL',
  [EMAIL_COMPONENT_IDS.PRESTATION]: 'EMAIL_BUILDER_COMPONENT_PRESTATION',
  [EMAIL_COMPONENT_IDS.FOOTER]: 'EMAIL_BUILDER_COMPONENT_FOOTER',
  [EMAIL_COMPONENT_IDS.HERO]: 'EMAIL_BUILDER_COMPONENT_HERO',
  [EMAIL_COMPONENT_IDS.THREE_COLUMNS]: 'EMAIL_BUILDER_COMPONENT_THREE_COLUMNS',
  [EMAIL_COMPONENT_IDS.STAT]: 'EMAIL_BUILDER_COMPONENT_STAT',
  [EMAIL_COMPONENT_IDS.DEADLINE]: 'EMAIL_BUILDER_COMPONENT_DEADLINE',
  [EMAIL_COMPONENT_IDS.PROMO_CODE]: 'EMAIL_BUILDER_COMPONENT_PROMO_CODE',
};

/**
 * Blocs proposés à l'ajout (+) SEULEMENT par un éditeur qui les active (`optInComponentIds`) : le bloc
 * Code promo n'a de sens que dans une campagne, qui porte la promotion (`promo:*`) ; les éditeurs de
 * notifications, de cartes cadeaux et du CRM ne le proposent pas.
 */
export const EMAIL_OPT_IN_COMPONENT_IDS: readonly EmailComponentId[] = [EMAIL_COMPONENT_IDS.PROMO_CODE];

export const EmailComponentInstanceSchema = Type.Object({
  id: Type.String(),
  componentId: Type.String(),
  variant: Type.String(),
  values: Type.Record(Type.String(), Type.Unknown()),
});
export type EmailComponentInstance = Static<typeof EmailComponentInstanceSchema>;

export const EmailConfigSchema = Type.Object({
  version: Type.String(),
  styles: EmailStylesSchema,
  previewText: Type.Optional(Type.String()),
  components: Type.Array(EmailComponentInstanceSchema),
});
export type EmailConfig = Static<typeof EmailConfigSchema>;
